import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { useTipawStore } from '@application/api';
import { useMediaQuery } from 'react-responsive';
import { BREAKPOINTS } from '@presentation/styles';
import { useRouter } from '@application/utils';
import { Button } from '../Button';
import * as Styled from './styled';
import type { StoreBannerProps, StoreBannerTheme } from './types';
import storeBannerLightBlue from './assets/storebanner_lightblue.webp';
import storeBannerLightBlueMobile from './assets/storebanner_lightblue_mobile.webp';

export * from './types';

const BACKGROUND_IMAGES: Record<'desktop' | 'mobile', Record<StoreBannerTheme, string>> = {
  desktop: {
    lightBlue: storeBannerLightBlue,
    blue: storeBannerLightBlue,
    light: storeBannerLightBlue,
  },
  mobile: {
    lightBlue: storeBannerLightBlueMobile,
    blue: storeBannerLightBlueMobile,
    light: storeBannerLightBlueMobile,
  },
};

export const StoreBanner: FC<StoreBannerProps> = (props) => {
  const { theme = 'lightBlue', source = 'homepage', useMobileImage = false } = props;

  const { onBannerClick } = useTipawStore();
  const { translate } = useRouter();
  const isMobile =
    useMediaQuery({ query: `(max-width: ${BREAKPOINTS.medium}px)` }) || useMobileImage;

  const backgroundImage = useMemo(() => {
    const imageType = isMobile ? 'mobile' : 'desktop';
    return BACKGROUND_IMAGES[imageType][theme];
  }, [isMobile, theme]);

  const onClick = useCallback(() => {
    onBannerClick(theme, source);
  }, [onBannerClick, source, theme]);

  const title = translate('common.storeBanners.lightBlue.title');
  const cta = translate('common.storeBanners.lightBlue.cta');
  return (
    <Styled.Container bgImage={backgroundImage} isMobile={isMobile} onClick={onClick}>
      <Styled.ContentContainer isMobile={isMobile}>
        <Styled.Title bannerTheme={theme}>{title}</Styled.Title>

        <Styled.ButtonContainer>
          <Button color="blue" shrink>
            {cta}
          </Button>
        </Styled.ButtonContainer>
      </Styled.ContentContainer>
    </Styled.Container>
  );
};
